import { Link } from '@tanstack/react-router';
import { T, Var, useGT } from 'gt-react';
import {
  CheckCircleIcon,
  CopyIcon,
  ListChecksIcon,
  MinusCircleIcon,
  XCircleIcon,
} from '@phosphor-icons/react';
import type { TaskResponse } from '@erp/rpc';
import {
  isTerminalTaskStatus,
  taskOutcomeBadges,
  type TaskOutcomeBadge,
} from '@erp/shared-types';
import useAuth from '@/api/auth/useAuth';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/toast';
import { cn } from '@/lib/utils';
import {
  PriorityDot,
  StatusPill,
} from '@/pages/authenticated/inbox/components/helpers';
import { groupInvoiceTasks } from './groupInvoiceTasks';
import { useInvoiceTasks } from './useInvoiceTasks';
import { useTaskTypeBadgeFields } from './useTaskTypeBadgeFields';

/**
 * Tasks whose anchor is this invoice, as a checklist: each parent with its
 * subtasks nested beneath it in the order they were created. Concluded work
 * stays in place, muted, so the analyst sees progress rather than a shrinking
 * list. Hidden when the viewer cannot read tasks, or when the invoice has
 * none.
 */
export function InvoiceTasksSection({ invoiceId }: { invoiceId: string }) {
  const { tasks, total, hasMore, canReadTasks, isLoading, error } =
    useInvoiceTasks(invoiceId);
  const { currentUser } = useAuth();
  const badgeFieldsFor = useTaskTypeBadgeFields(canReadTasks);
  const badgesFor = (task: TaskResponse) =>
    taskOutcomeBadges(
      badgeFieldsFor(task.taskTypeId),
      task.outcome,
      task.outcomeData,
    );

  if (!canReadTasks) return null;
  if (!isLoading && !error && tasks.length === 0) return null;

  const groups = groupInvoiceTasks(tasks);
  const openCount = tasks.filter(
    (task) => !isTerminalTaskStatus(task.status),
  ).length;

  return (
    <section
      className="space-y-1.5 rounded-md border p-3"
      data-testid="invoice-tasks"
    >
      <h3 className="flex items-center gap-1.5 text-sm font-medium">
        <ListChecksIcon className="size-4 text-muted-foreground" />
        <T context="invoice slide-in: heading over tasks anchored to this invoice">
          Tasks
        </T>
        {openCount > 0 && (
          <Badge variant="secondary" className="ml-1">
            {openCount}
          </Badge>
        )}
      </h3>
      {isLoading && (
        <p className="text-xs text-muted-foreground">
          <T context="invoice slide-in: tasks section loading">
            Loading tasks…
          </T>
        </p>
      )}
      {error && (
        <p className="text-xs text-destructive">
          {error instanceof Error ? error.message : String(error)}
        </p>
      )}
      {groups.length > 0 && (
        <ul className="space-y-0.5">
          {groups.map(({ task, subtasks, completedSubtasks }) => (
            <li key={task.id}>
              <InvoiceTaskRow
                task={task}
                currentUserId={currentUser?.id}
                badges={badgesFor(task)}
                progress={
                  subtasks.length > 0
                    ? { done: completedSubtasks, total: subtasks.length }
                    : undefined
                }
              />
              {subtasks.length > 0 && (
                <ul className="ml-[13px] space-y-0.5 border-l pl-2">
                  {subtasks.map((subtask) => (
                    <li key={subtask.id}>
                      <InvoiceTaskRow
                        task={subtask}
                        currentUserId={currentUser?.id}
                        badges={badgesFor(subtask)}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
      {hasMore && (
        <p className="text-xs text-muted-foreground">
          <T context="invoice slide-in: the anchored task list is truncated to the most recently updated">
            Showing <Var>{tasks.length}</Var> of <Var>{total}</Var>
          </T>
        </p>
      )}
    </section>
  );
}

function InvoiceTaskRow({
  task,
  currentUserId,
  badges,
  progress,
}: {
  task: TaskResponse;
  currentUserId: string | undefined;
  /** Outcome values the task type flags "Show as badge", shown under the title. */
  badges: TaskOutcomeBadge[];
  /** Subtask completion shown beside a parent's title. */
  progress?: { done: number; total: number };
}) {
  return (
    <>
      <InvoiceTaskLink
        task={task}
        currentUserId={currentUserId}
        progress={progress}
      />
      {badges.length > 0 && <TaskOutcomeBadges badges={badges} />}
    </>
  );
}

function InvoiceTaskLink({
  task,
  currentUserId,
  progress,
}: {
  task: TaskResponse;
  currentUserId: string | undefined;
  progress?: { done: number; total: number };
}) {
  const closed = isTerminalTaskStatus(task.status);
  return (
    <Link
      to="/tasks/$taskId"
      params={{ taskId: task.id }}
      className={cn(
        'flex items-center gap-2 rounded-md px-1.5 py-1 hover:bg-muted/50',
        task.status === 'needs_input' && 'bg-amber-50 dark:bg-amber-950/30',
        closed && 'text-muted-foreground',
      )}
    >
      <TaskMarker task={task} />
      <span
        className={cn(
          'min-w-0 flex-1 truncate text-sm',
          closed ? 'font-normal' : 'font-medium',
        )}
      >
        {task.title}
      </span>
      {progress && (
        <span
          className="shrink-0 text-[11px] tabular-nums text-muted-foreground"
          data-testid="invoice-task-progress"
        >
          <T context="invoice slide-in: subtasks completed out of total, shown beside a parent task">
            <Var>{progress.done}</Var>/<Var>{progress.total}</Var> done
          </T>
        </span>
      )}
      {task.displayId && (
        <span className="shrink-0 font-mono text-[10px] text-muted-foreground">
          {task.displayId}
        </span>
      )}
      {task.outcome && (
        <span className="shrink-0 text-[11px] capitalize text-muted-foreground">
          {task.outcome}
        </span>
      )}
      {currentUserId !== undefined && task.assigneeUserId === currentUserId && (
        <Badge variant="secondary" className="shrink-0 text-[10px]">
          <T context="badge on an invoice task assigned to the current user">
            You
          </T>
        </Badge>
      )}
      <StatusPill status={task.status} />
    </Link>
  );
}

/**
 * Recorded outcome values as label/value chips, aligned with the task title.
 * Clicking one copies its value (e.g. a voucher number to paste elsewhere).
 */
function TaskOutcomeBadges({ badges }: { badges: TaskOutcomeBadge[] }) {
  const gt = useGT();
  return (
    <div
      className="flex flex-wrap gap-1 pb-1 pl-[30px] pr-1.5"
      data-testid="invoice-task-outcome-badges"
    >
      {badges.map((badge) => (
        <button
          key={badge.key}
          type="button"
          className="group inline-flex max-w-full items-center gap-1 rounded-md border bg-muted/40 px-1.5 py-0.5 text-[11px] hover:bg-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          title={gt('Copy {label}', {
            context: 'tooltip: copy a task outcome value shown as a badge',
            label: badge.label,
          })}
          onClick={() => {
            // Deferred so a missing clipboard API (insecure context) lands
            // in the error toast instead of throwing from the handler.
            void Promise.resolve()
              .then(() => navigator.clipboard.writeText(badge.value))
              .then(
                () =>
                  toast.success(
                    gt('{label} copied', {
                      context: 'toast after copying a task outcome badge value',
                      label: badge.label,
                    }),
                  ),
                () =>
                  toast.error(
                    gt('Unable to copy result', {
                      context: 'task outcome clipboard error',
                    }),
                  ),
              );
          }}
        >
          <span className="shrink-0 text-muted-foreground">{badge.label}</span>
          <span className="truncate font-mono font-medium">{badge.value}</span>
          <CopyIcon
            className="size-3 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100"
            aria-hidden="true"
          />
        </button>
      ))}
    </div>
  );
}

/**
 * Leading glyph: priority while the task is open, how it ended once closed.
 */
function TaskMarker({ task }: { task: TaskResponse }) {
  switch (task.status) {
    case 'completed':
      return (
        <CheckCircleIcon
          weight="fill"
          className="size-3.5 shrink-0 text-emerald-600"
          aria-hidden="true"
        />
      );
    case 'failed':
      return (
        <XCircleIcon
          weight="fill"
          className="size-3.5 shrink-0 text-red-600"
          aria-hidden="true"
        />
      );
    case 'cancelled':
      return (
        <MinusCircleIcon
          weight="fill"
          className="size-3.5 shrink-0 text-stone-400"
          aria-hidden="true"
        />
      );
    default:
      return (
        <span className="flex size-3.5 shrink-0 items-center justify-center">
          <PriorityDot priority={task.priority} />
        </span>
      );
  }
}
